import { useQuery } from '@tanstack/react-query';
import { dashboardApi } from '../../api/dashboard';
import { useToast } from '../toast/Toast';

export function RecommendationsPanel() {
  const { show } = useToast();
  const { data, isLoading } = useQuery({
    queryKey: ['dashboard', 'recommendations-summary'],
    queryFn: dashboardApi.recommendationsSummary,
    refetchInterval: 60_000,
  });

  return (
    <div className="col-span-2 bg-white border border-cream-200 rounded-sm">
      <div className="px-5 py-4 border-b border-cream-100 flex items-center justify-between">
        <div>
          <h3 className="serif-display text-base text-navy-800 font-medium accent-underline inline-block">
            AI-Generated Insights
          </h3>
          <p className="text-xs text-navy-800/60 mt-3">
            Acceptance rate {data ? `${data.stats.acceptance_rate}%` : '—'} · Click any to take action
          </p>
        </div>
        {data && (
          <span className="text-[10px] uppercase tracking-widest text-navy-800/40 font-medium">
            {data.stats.open} actionable
          </span>
        )}
      </div>

      <div className="divide-y divide-cream-100">
        {isLoading || !data ? (
          Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="px-5 py-4">
              <div className="h-3 w-3/4 bg-cream-200 rounded animate-pulse" />
              <div className="h-2 w-1/3 bg-cream-200 rounded animate-pulse mt-2" />
            </div>
          ))
        ) : (
          data.open.map((r, idx) => (
            <div key={r.id} className="px-5 py-4 hover:bg-cream-50">
              <div className="flex items-start gap-3">
                <span className="w-7 h-7 rounded-full bg-saffron-100 text-saffron-700 flex items-center justify-center flex-shrink-0 text-xs font-semibold">
                  {String(idx + 1).padStart(2, '0')}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-navy-800 leading-snug">{r.text}</div>
                  <div className="mt-2 flex items-center gap-3 text-[11px]">
                    <span className="text-navy-800/50 font-numeric">Confidence {r.confidence}%</span>
                    <span className="text-navy-800/30">·</span>
                    <span className="text-navy-800/50">For: <span className="text-navy-800/70 font-medium">{r.target}</span></span>
                    <button
                      type="button"
                      onClick={() => show(`${r.primary_action} confirmed · Audit log entry created`)}
                      className="ml-auto text-saffron-600 hover:text-saffron-700 font-medium"
                    >
                      {r.primary_action} →
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export function ActivityFeed() {
  const { show } = useToast();
  const { data, isLoading } = useQuery({
    queryKey: ['dashboard', 'activity-feed', 10],
    queryFn: () => dashboardApi.activityFeed(10),
    refetchInterval: 20_000,
  });

  return (
    <div className="bg-white border border-cream-200 rounded-sm">
      <div className="px-5 py-4 border-b border-cream-100">
        <h3 className="serif-display text-base text-navy-800 font-medium accent-underline inline-block">Activity</h3>
        <p className="text-xs text-navy-800/60 mt-3">Department-wide audit feed</p>
      </div>
      <div className="px-5 py-2" style={{ maxHeight: 420, overflowY: 'auto' }}>
        {isLoading || !data ? (
          Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="py-3 border-b border-cream-50">
              <div className="h-3 w-3/4 bg-cream-200 rounded animate-pulse" />
            </div>
          ))
        ) : data.length === 0 ? (
          <div className="py-6 text-xs text-navy-800/50 text-center">No activity yet</div>
        ) : (
          data.map((a) => (
            <button
              key={a.id}
              type="button"
              onClick={() => show('Audit entry detail — full payload coming in Day 7')}
              className="w-full text-left py-3 border-b border-cream-50 flex gap-3 hover:bg-cream-50 -mx-2 px-2 rounded-sm"
            >
              <div className="text-[10px] text-navy-800/40 font-numeric pt-0.5 w-12 flex-shrink-0">{a.time}</div>
              <div className="text-xs leading-snug">
                <span className="font-medium text-navy-800">{a.actor}</span>{' '}
                <span className="text-navy-800/70">{a.action}</span>
                {a.target && <span className="text-navy-800/50"> · {a.target}</span>}
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );
}
